import { getSession } from "next-auth/react";
import { getApiBaseUrl } from "../utils/index";
import { clearSessionAndRedirect } from "../utils/cleanup";
import { getTraceId, TRACE_ID_HEADER, createLogger } from "./logger";

const log = createLogger("API");

// Base API URL
export const API_BASE = getApiBaseUrl();

// Extended session interface
interface ExtendedSession {
  accessToken?: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    role: string;
  };
}

// API response interface
export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  status: number;
}

export class ApiClient {
  private baseUrl: string;
  private isServer: boolean;
  // Token caching to avoid repeated getSession() calls
  private cachedToken: string | null = null;
  private tokenCacheExpiry: number = 0;
  private static TOKEN_CACHE_TTL = 60000; // 60 seconds

  constructor(baseUrl: string = API_BASE, isServer: boolean = false) {
    this.baseUrl = baseUrl;
    this.isServer = isServer;
  }

  // Clear cached token (call on logout)
  public clearTokenCache(): void {
    this.cachedToken = null;
    this.tokenCacheExpiry = 0;
  }

  // Get authentication token with caching
  private async getAuthToken(): Promise<string | null> {
    const now = Date.now();

    // Return cached token if still valid (client-side only)
    if (!this.isServer && this.cachedToken && now < this.tokenCacheExpiry) {
      return this.cachedToken;
    }

    try {
      if (this.isServer) {
        // Loaded here so next-auth's server code (jose, openid-client, ...)
        // stays out of the client bundle
        const [{ getServerSession }, { authOptions }] = await Promise.all([
          import("next-auth/next"),
          import("./auth"),
        ]);
        const session = (await getServerSession(
          authOptions
        )) as ExtendedSession | null;
        return session?.accessToken || null;
      } else {
        const session = (await getSession()) as ExtendedSession | null;
        const token = session?.accessToken || null;
        // Cache the token for subsequent requests
        this.cachedToken = token;
        this.tokenCacheExpiry = now + ApiClient.TOKEN_CACHE_TTL;
        return token;
      }
    } catch (error) {
      log.error("Failed to get auth token", error);
      return null;
    }
  }

  // Create fetch options with authentication
  private async createFetchOptions(
    method: string,
    body?: unknown,
    requireAuth: boolean = false,
    isMultipart: boolean = false,
    authMode: "required" | "optional" = "required"
  ): Promise<RequestInit> {
    const headers: Record<string, string> = {};

    if (!isMultipart) {
      headers["Content-Type"] = "application/json";
    }
    headers["Accept"] = "application/json";

    // Add trace ID for end-to-end correlation
    headers[TRACE_ID_HEADER] = getTraceId();

    if (requireAuth) {
      const token = await this.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      } else if (authMode === "required") {
        if (!this.isServer) {
          await clearSessionAndRedirect(
            "Authentication required but no token available"
          );
        }
        throw new Error("Authentication required but no token available");
      }
    }

    return {
      method,
      headers,
      body: body
        ? isMultipart
          ? (body as FormData)
          : JSON.stringify(body)
        : undefined,
    };
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    options: {
      method: string;
      body?: unknown;
      requireAuth?: boolean;
      isMultipart?: boolean;
      useNextApi?: boolean;
      authMode?: "required" | "optional";
    }
  ): Promise<T> {
    const {
      method,
      body,
      requireAuth = false,
      isMultipart = false,
      useNextApi = false,
      authMode = "required",
    } = options;

    try {
      const fetchOptions = await this.createFetchOptions(
        method,
        body,
        requireAuth,
        isMultipart,
        authMode
      );
      const url = useNextApi ? endpoint : `${this.baseUrl}${endpoint}`;

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        // Extract error message from response body if available
        let errorMessage = `Request failed with status ${response.status}`;
        let errorBody: Record<string, unknown> | undefined;
        let errorCode: string | undefined;
        try {
          const errorContentType = response.headers.get("content-type");
          if (errorContentType?.includes("application/json")) {
            errorBody = await response.json();
            const detail = errorBody?.detail;
            // FastAPI puts the payload under `detail`. It is either a plain
            // string or, for user-facing rejections, an object carrying a
            // stable `code` alongside the English `message`. Without this the
            // whole JSON body was stringified into the user-visible message.
            if (detail && typeof detail === "object") {
              const d = detail as Record<string, unknown>;
              if (typeof d.code === "string") errorCode = d.code;
              if (typeof d.message === "string") errorMessage = d.message;
            } else if (typeof detail === "string") {
              errorMessage = detail;
            } else {
              const msg = errorBody && (errorBody.message || errorBody.error);
              errorMessage =
                (typeof msg === "string" ? msg : null) ||
                JSON.stringify(errorBody);
            }
            if (typeof errorBody?.code === "string") {
              errorCode = errorCode || (errorBody.code as string);
            }
          } else {
            errorMessage = await response.text();
          }
        } catch {
          // Ignore body parsing errors and use default message
        }

        if (response.status === 401) {
          if (requireAuth && authMode !== "optional") {
            if (!this.isServer) {
              await clearSessionAndRedirect(
                "Your session has expired. Please sign in again."
              );
            }
          }
          const authError = new Error(errorMessage || "Authentication failed");
          (authError as unknown as { status?: number }).status = 401;
          throw authError;
        }

        const error = new Error(errorMessage || `HTTP ${response.status}`);
        // Attach status and parsed body for callers that need them (e.g. 409 conflict)
        (error as unknown as { status?: number }).status = response.status;
        if (errorBody) {
          (error as unknown as { body?: Record<string, unknown> }).body =
            errorBody;
        }
        // Stable machine-readable code so callers can render localised copy
        // instead of the backend's English sentence.
        if (errorCode) {
          (error as unknown as { code?: string }).code = errorCode;
        }
        throw error;
      }

      // Handle different response types
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        return await response.json();
      } else if (
        contentType?.includes("image/") ||
        contentType?.includes("application/octet-stream") ||
        contentType?.includes("application/pdf") ||
        contentType?.includes("application/msword") ||
        contentType?.includes(
          "application/vnd.openxmlformats-officedocument"
        ) ||
        contentType?.includes("text/plain")
      ) {
        return (await response.blob()) as T;
      } else {
        // For any unknown content type that might be a file, try blob first
        try {
          return (await response.blob()) as T;
        } catch {
          return (await response.text()) as T;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const status = (error as { status?: number }).status;

      // Don't log 401 errors when authMode is optional - these are expected
      if (status === 401 && authMode === "optional") {
        // Silently re-throw - caller will handle gracefully
        throw error;
      }

      if (message.includes("429")) {
        log.warn("Request rate-limited", { endpoint, status: 429 });
      } else {
        log.error("Request failed", error, { endpoint, method, status });
      }
      throw error;
    }
  }

  // Public API methods (no authentication required)
  async get<T>(endpoint: string, useNextApi: boolean = false): Promise<T> {
    return this.request<T>(endpoint, { method: "GET", useNextApi });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, { method: "POST", body, useNextApi });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, { method: "PUT", body, useNextApi });
  }

  async delete<T>(endpoint: string, useNextApi: boolean = false): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE", useNextApi });
  }

  // Authenticated API methods
  async getAuth<T>(endpoint: string, useNextApi: boolean = false): Promise<T> {
    return this.request<T>(endpoint, {
      method: "GET",
      requireAuth: true,
      useNextApi,
    });
  }

  async getAuthOptional<T>(
    endpoint: string,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "GET",
      requireAuth: true,
      useNextApi,
      authMode: "optional",
    });
  }

  async postAuth<T>(
    endpoint: string,
    body?: unknown,
    useNextApiOrOptions?:
      | boolean
      | {
          useNextApi?: boolean;
          authMode?: "required" | "optional";
        }
  ): Promise<T> {
    const options =
      typeof useNextApiOrOptions === "boolean"
        ? { useNextApi: useNextApiOrOptions }
        : (useNextApiOrOptions ?? {});

    const { useNextApi = false, authMode = "required" } = options;

    return this.request<T>(endpoint, {
      method: "POST",
      body,
      requireAuth: true,
      useNextApi,
      authMode,
    });
  }

  async putAuth<T>(
    endpoint: string,
    body?: unknown,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body,
      requireAuth: true,
      useNextApi,
    });
  }

  async patchAuth<T>(
    endpoint: string,
    body?: unknown,
    useNextApiOrOptions?:
      | boolean
      | {
          useNextApi?: boolean;
          authMode?: "required" | "optional";
        }
  ): Promise<T> {
    const options =
      typeof useNextApiOrOptions === "boolean"
        ? { useNextApi: useNextApiOrOptions }
        : (useNextApiOrOptions ?? {});

    const { useNextApi = false, authMode = "required" } = options;

    return this.request<T>(endpoint, {
      method: "PATCH",
      body,
      requireAuth: true,
      useNextApi,
      authMode,
    });
  }

  async deleteAuth<T>(
    endpoint: string,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      requireAuth: true,
      useNextApi,
    });
  }

  // Multipart upload method
  async uploadFile<T>(
    endpoint: string,
    formData: FormData,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
      requireAuth: true,
      isMultipart: true,
      useNextApi,
    });
  }

  // Multipart upload method (no authentication)
  async uploadFilePublic<T>(
    endpoint: string,
    formData: FormData,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
      requireAuth: false,
      isMultipart: true,
      useNextApi,
    });
  }

  // Multipart upload with PUT (authenticated)
  async uploadFilePutAuth<T>(
    endpoint: string,
    formData: FormData,
    useNextApi: boolean = false
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: formData,
      requireAuth: true,
      isMultipart: true,
      useNextApi,
    });
  }
}

// Create instances for different contexts
export const clientApi = new ApiClient(API_BASE, false); // Client-side
export const serverApi = new ApiClient(API_BASE, true); // Server-side

// Helper function to handle API errors consistently
export const handleApiError = async (error: unknown): Promise<string> => {
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes("429")) {
    log.warn("Rate-limited", { message });
  } else {
    log.error("Handler caught error", error);
  }

  if (error instanceof Error) {
    if (error.message === "Authentication failed") {
      // Use unified session clearing
      if (typeof window !== "undefined") {
        await clearSessionAndRedirect(
          "Your session has expired. Please sign in again."
        );
      }
      return "Authentication failed. Please sign in again.";
    }
    return error.message;
  }

  return "An unexpected error occurred";
};

/**
 * Read the stable machine-readable error code the API attaches to user-facing
 * rejections (see `detail.code` on the backend). Returns undefined for errors
 * that carry no code, in which case callers fall back to `error.message`.
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
};

/**
 * Rethrow an API error while preserving the fields that would otherwise be lost
 * when a helper collapses it into `new Error(message)`.
 */
export const rethrowWithApiErrorCode = (
  error: unknown,
  message: string
): never => {
  const rethrown = new Error(message);
  const code = getApiErrorCode(error);
  if (code) (rethrown as unknown as { code?: string }).code = code;
  const status = (error as { status?: unknown })?.status;
  if (typeof status === "number") {
    (rethrown as unknown as { status?: number }).status = status;
  }
  throw rethrown;
};
